import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./Checkout.module.css";
import { useCart } from "../../Context/CartContext";

// ─── Order Summary Sidebar ──────────────────────────────────────────────────
const OrderSummary = ({ cart, cartTotal }) => {
  const subtotalINR = cartTotal * 83;
  const shippingINR = cart.length === 0 ? 0 : (subtotalINR >= 500 ? 0 : 49);
  const totalINR    = subtotalINR + shippingINR;

  return (
    <div className={styles.summary}>
      <h3 className={styles.summaryTitle}>Order Summary</h3>
      {cart.length === 0 ? (
        <p style={{ fontSize: "0.88rem", color: "#94a3b8", textAlign: "center", padding: "1rem 0" }}>No items selected</p>
      ) : (
        cart.map((item) => (
          <div key={item._id} className={styles.summaryRow}>
            <span style={{ maxWidth: "190px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {item.title} × {item.qty}
            </span>
            <span style={{ fontWeight: 700, color: "#0f172a" }}>₹{((item.price * 83) * item.qty).toFixed(0)}</span>
          </div>
        ))
      )}
      <hr className={styles.summaryDivider} />
      <div className={styles.summaryRow}><span>Subtotal</span><span>₹{subtotalINR.toFixed(0)}</span></div>
      <div className={styles.summaryRow}>
        <span>Shipping</span>
        <span style={{ color: shippingINR === 0 ? "#2d6a4f" : undefined }}>{cart.length === 0 ? "₹0" : (shippingINR === 0 ? "FREE" : `₹${shippingINR}`)}</span>
      </div>
      {cart.length > 0 && shippingINR > 0 && (
        <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "8px" }}>
          Add ₹{(500 - subtotalINR).toFixed(0)} more for free shipping
        </div>
      )}
      <hr className={styles.summaryDivider} />
      <div className={styles.summaryTotal}>
        <span>Total</span>
        <span>₹{totalINR.toFixed(0)}</span>
      </div>
      <div style={{ marginTop: "1rem", background: "#f0fdf4", borderRadius: "10px", padding: "0.75rem", fontSize: "0.8rem", color: "#166534", fontWeight: 700 }}>
        🔒 Secure & Encrypted Checkout
      </div>
    </div>
  );
};

// ─── Step 1: Cart ───────────────────────────────────────────────────────────
const CartStep = ({ cart, updateQuantity, removeFromCart, onNext }) => {
  if (cart.length === 0) {
    return (
      <div className={styles.panel}>
        <div className={styles.emptyCart}>
          <div style={{ fontSize: "3.5rem", marginBottom: "1rem" }}>🛒</div>
          <h2 style={{ fontWeight: 900, color: "#0f172a", margin: "0 0 8px" }}>Your cart is empty</h2>
          <p>Browse our wellness products and add items to your cart.</p>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>Your Cart ({cart.length} items)</h2>
      {cart.map((item) => (
        <div key={item._id} className={styles.cartItem}>
          <img
            src={item.coverImage || "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=200&q=60"}
            alt={item.title}
            className={styles.itemImg}
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=200&q=60"; }}
          />
          <div className={styles.itemInfo}>
            <p className={styles.itemTitle}>{item.title}</p>
            <p className={styles.itemCat}>{item.category}</p>
            <div className={styles.qtyRow}>
              <button className={styles.qtyBtn} onClick={() => updateQuantity(item._id, item.qty - 1)}>−</button>
              <span className={styles.qtyNum}>{item.qty}</span>
              <button className={styles.qtyBtn} onClick={() => updateQuantity(item._id, item.qty + 1)}>+</button>
              <button className={styles.removeBtn} onClick={() => removeFromCart(item._id)}>🗑 Remove</button>
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div className={styles.itemPrice}>₹{(item.price * 83).toFixed(0)}</div>
            <div className={styles.itemSubtotal}>× {item.qty} = ₹{((item.price * 83) * item.qty).toFixed(0)}</div>
          </div>
        </div>
      ))}
      <button className={styles.primaryBtn} onClick={onNext}>Proceed to Shipping →</button>
    </div>
  );
};

// ─── Step 2: Shipping ───────────────────────────────────────────────────────
const ShippingStep = ({ shipping, setShipping, onNext, onBack }) => {
  const handleChange = (field, val) => setShipping((p) => ({ ...p, [field]: val }));
  const valid = shipping.fullName && shipping.phone && shipping.street && shipping.city && shipping.state && shipping.zip;
  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>Shipping Details</h2>
      <div className={styles.formGrid}>
        <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
          <label className={styles.label}>Full Name *</label>
          <input className={styles.input} placeholder="Ananya Sharma" value={shipping.fullName} onChange={(e) => handleChange("fullName", e.target.value)} />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Phone Number *</label>
          <input className={styles.input} placeholder="+91 98765 43210" value={shipping.phone} onChange={(e) => handleChange("phone", e.target.value)} />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Email</label>
          <input className={styles.input} type="email" placeholder="you@email.com" value={shipping.email} onChange={(e) => handleChange("email", e.target.value)} />
        </div>
        <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
          <label className={styles.label}>Street Address *</label>
          <input className={styles.input} placeholder="123, MG Road, Apartment 4B" value={shipping.street} onChange={(e) => handleChange("street", e.target.value)} />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>City *</label>
          <input className={styles.input} placeholder="Mumbai" value={shipping.city} onChange={(e) => handleChange("city", e.target.value)} />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>State *</label>
          <input className={styles.input} placeholder="Maharashtra" value={shipping.state} onChange={(e) => handleChange("state", e.target.value)} />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>PIN Code *</label>
          <input className={styles.input} placeholder="400001" value={shipping.zip} onChange={(e) => handleChange("zip", e.target.value)} />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Country</label>
          <input className={styles.input} value="India" readOnly style={{ background: "#f1f5f9", color: "#94a3b8" }} />
        </div>
      </div>
      <button className={styles.primaryBtn} onClick={onNext} disabled={!valid} style={{ marginTop: "1.5rem" }}>
        Continue to Payment →
      </button>
      <button className={styles.ghostBtn} onClick={onBack}>← Back to Cart</button>
    </div>
  );
};

// ─── Step 3: Payment ────────────────────────────────────────────────────────
const PaymentStep = ({ card, setCard, paymentMethod, setPaymentMethod, onPay, paying, onBack }) => {
  const handleChange = (field, val) => setCard((p) => ({ ...p, [field]: val }));

  const formatCardNum = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };
  const formatExpiry = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length > 2) return digits.slice(0, 2) + "/" + digits.slice(2);
    return digits;
  };

  const displayNum = card.number ? card.number.replace(/\D/g, "").padEnd(16, "·").replace(/(.{4})/g, "$1 ").trim() : "•••• •••• •••• ••••";
  const validOnline = card.number.replace(/\s/g, "").length === 16 && card.expiry.length === 5 && card.cvv.length === 3 && card.name;
  const isValid = paymentMethod === "COD" ? true : validOnline;

  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>Select Payment Method</h2>
      
      {/* Payment Method Selector Toggle */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.5rem" }}>
        <div
          onClick={() => setPaymentMethod("Online")}
          style={{
            padding: "1rem",
            border: paymentMethod === "Online" ? "2px solid #2d6a4f" : "1px solid #cbd5e1",
            background: paymentMethod === "Online" ? "#f0fdf4" : "#ffffff",
            borderRadius: "12px",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
            <span>💳</span> Online Payment
          </div>
          <div style={{ fontSize: "0.78rem", color: "#64748b", marginTop: "4px" }}>Card, UPI, or NetBanking</div>
        </div>

        <div
          onClick={() => setPaymentMethod("COD")}
          style={{
            padding: "1rem",
            border: paymentMethod === "COD" ? "2px solid #2d6a4f" : "1px solid #cbd5e1",
            background: paymentMethod === "COD" ? "#f0fdf4" : "#ffffff",
            borderRadius: "12px",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
            <span>💵</span> Cash on Delivery
          </div>
          <div style={{ fontSize: "0.78rem", color: "#64748b", marginTop: "4px" }}>Pay at your doorstep</div>
        </div>
      </div>

      {paymentMethod === "COD" ? (
        <div style={{ background: "#f8fafc", border: "1.5px dashed #94a3b8", borderRadius: "12px", padding: "1.75rem", margin: "1rem 0 1.5rem", textAlign: "center" }}>
          <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>📦💵</div>
          <h3 style={{ margin: "0 0 8px", color: "#0f172a", fontWeight: 800, fontSize: "1.2rem" }}>Cash on Delivery (COD) Selected</h3>
          <p style={{ margin: "0 auto", color: "#475569", fontSize: "0.92rem", lineHeight: "1.6", maxWidth: "420px" }}>
            No advance payment needed! You can conveniently pay via Cash or UPI QR code directly to our delivery agent when your wellness package arrives.
          </p>
        </div>
      ) : (
        <>
          {/* Visual credit card */}
          <div className={styles.creditCard}>
            <div className={styles.cardChip}>💳</div>
            <div className={styles.cardNumber}>{displayNum}</div>
            <div className={styles.cardFooterRow}>
              <div>
                <div className={styles.cardLabel}>Card Holder</div>
                <div className={styles.cardValue}>{card.name || "YOUR NAME"}</div>
              </div>
              <div>
                <div className={styles.cardLabel}>Expires</div>
                <div className={styles.cardValue}>{card.expiry || "MM/YY"}</div>
              </div>
            </div>
          </div>

          <div className={styles.secureTag}>🔒 256-bit SSL Encrypted — Your card data is never stored</div>

          <div className={styles.formGrid}>
            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
              <label className={styles.label}>Name on Card</label>
              <input className={styles.input} placeholder="ANANYA SHARMA" value={card.name} onChange={(e) => handleChange("name", e.target.value.toUpperCase())} />
            </div>
            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
              <label className={styles.label}>Card Number</label>
              <input className={styles.input} placeholder="1234 5678 9012 3456" value={card.number} maxLength={19}
                onChange={(e) => handleChange("number", formatCardNum(e.target.value))} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Expiry Date</label>
              <input className={styles.input} placeholder="MM/YY" value={card.expiry} maxLength={5}
                onChange={(e) => handleChange("expiry", formatExpiry(e.target.value))} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>CVV</label>
              <input className={styles.input} placeholder="•••" type="password" maxLength={3}
                onChange={(e) => handleChange("cvv", e.target.value.replace(/\D/g, "").slice(0, 3))} />
            </div>
          </div>

          {/* Accepted cards */}
          <div style={{ display: "flex", gap: "8px", marginTop: "1rem", flexWrap: "wrap" }}>
            {["💳 Visa", "💳 Mastercard", "💳 Rupay", "💳 Amex", "UPI", "Net Banking"].map((m) => (
              <span key={m} style={{ fontSize: "0.75rem", padding: "4px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", color: "#475569", fontWeight: 600 }}>{m}</span>
            ))}
          </div>
        </>
      )}

      <button className={styles.primaryBtn} onClick={onPay} disabled={!isValid || paying} style={{ marginTop: "1.5rem" }}>
        {paying ? "⏳ Processing order..." : paymentMethod === "COD" ? "💵 Place Order (Cash on Delivery)" : "🔒 Pay Securely"}
      </button>
      <button className={styles.ghostBtn} onClick={onBack}>← Back to Shipping</button>
    </div>
  );
};

// ─── Success Screen ─────────────────────────────────────────────────────────
const SuccessScreen = ({ orderId, transactionId, totalAmount, paymentMethod, paymentStatus }) => {
  const navigate = useNavigate();
  return (
    <div className={styles.successPage}>
      <div className={styles.successIcon}>✓</div>
      <h1 className={styles.successTitle}>Order Placed! 🎉</h1>
      <p className={styles.successSubtitle}>
        Your order has been confirmed and will be dispatched within 24 hours. A receipt and delivery details have been sent to your registered email address.
      </p>
      <div className={styles.successDetails}>
        <div className={styles.successDetail}><span>Order ID</span><strong>#{String(orderId).slice(-8).toUpperCase()}</strong></div>
        <div className={styles.successDetail}><span>Payment Method</span><strong>{paymentMethod === "COD" ? "💵 Cash on Delivery" : "💳 Online Payment"}</strong></div>
        <div className={styles.successDetail}><span>Transaction</span><strong style={{ fontSize: "0.82rem" }}>{transactionId}</strong></div>
        <div className={styles.successDetail}><span>Amount Total</span><strong style={{ color: "#2d6a4f" }}>₹{((totalAmount * 83) >= 500 ? (totalAmount * 83) : (totalAmount * 83) + 49).toFixed(0)}</strong></div>
        <div className={styles.successDetail}><span>Status</span><strong style={{ color: paymentMethod === "COD" ? "#d97706" : "#166534" }}>{paymentMethod === "COD" ? "⏳ Pending (Pay on Delivery)" : "✅ Paid — Processing"}</strong></div>
        <div className={styles.successDetail}><span>Est. Delivery</span><strong>3–5 Business Days</strong></div>
      </div>
      <button
        onClick={() => navigate("/shop")}
        style={{ padding: "0.85rem 2.5rem", background: "#2d6a4f", color: "#ffffff", border: "none", borderRadius: "9999px", fontWeight: 800, fontSize: "1rem", cursor: "pointer", fontFamily: "'Inter', sans-serif", boxShadow: "0 6px 20px rgba(45,106,79,0.3)", marginRight: "12px" }}
      >
        Continue Shopping →
      </button>
      <button
        onClick={() => navigate("/patient-dashboard")}
        style={{ padding: "0.85rem 2rem", background: "transparent", color: "#2d6a4f", border: "1.5px solid #2d6a4f", borderRadius: "9999px", fontWeight: 700, fontSize: "0.95rem", cursor: "pointer", fontFamily: "'Inter', sans-serif", marginTop: "12px" }}
      >
        Back to Dashboard
      </button>
    </div>
  );
};

// ─── Main CheckoutFlow component ────────────────────────────────────────────
const CheckoutFlow = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, clearCart, cartTotal } = useCart();

  const [step, setStep] = useState(1);
  const [shipping, setShipping] = useState({ fullName: "", phone: "", email: "", street: "", city: "", state: "", zip: "", country: "India" });
  const [card, setCard]         = useState({ name: "", number: "", expiry: "", cvv: "" });
  const [paymentMethod, setPaymentMethod] = useState("Online");
  const [paying, setPaying]     = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  const handlePay = async () => {
    setPaying(true);
    try {
      const token     = localStorage.getItem("token");
      const patientId = localStorage.getItem("userId");
      const payload   = {
        patientId,
        paymentMethod,
        cart: cart.map((item) => ({ productId: item._id, qty: item.qty })),
        shippingAddress: {
          fullName: shipping.fullName,
          phone:   shipping.phone,
          street:  `${shipping.street}`,
          city:    shipping.city,
          state:   shipping.state,
          zip:     shipping.zip,
          country: shipping.country || "India",
        },
      };
      const res = await axios.post("/api/shop/checkout", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status) {
        clearCart();
        setOrderResult(res.data);
        setStep(4);
      }
    } catch (err) {
      alert("Payment failed. Please try again.");
      console.error(err);
    } finally {
      setPaying(false);
    }
  };

  const stepLabels = ["Cart", "Shipping", "Payment"];

  if (step === 4 && orderResult) {
    return <SuccessScreen orderId={orderResult.orderId} transactionId={orderResult.transactionId} totalAmount={orderResult.totalAmount} paymentMethod={orderResult.paymentMethod} paymentStatus={orderResult.paymentStatus} />;
  }

  return (
    <div className={styles.page}>
      {/* Top nav */}
      <div className={styles.topNav}>
        <button className={styles.backBtn} onClick={() => navigate("/shop")}>← Shop</button>
        <span className={styles.topNavTitle}>🛒 Checkout</span>
      </div>

      {/* Steps bar */}
      <div className={styles.stepsBar}>
        {stepLabels.map((label, idx) => {
          const n = idx + 1;
          const isActive = step === n;
          const isDone   = step > n;
          return (
            <React.Fragment key={n}>
              {idx > 0 && <div className={`${styles.stepConnector} ${isDone ? styles.stepConnectorDone : ""}`} />}
              <div className={`${styles.step} ${isActive ? styles.stepActive : ""} ${isDone ? styles.stepDone : ""}`}>
                <div className={styles.stepNum}>{isDone ? "✓" : n}</div>
                {label}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Main content */}
      <div className={styles.main}>
        <div>
          {step === 1 && <CartStep cart={cart} updateQuantity={updateQuantity} removeFromCart={removeFromCart} onNext={() => cart.length > 0 && setStep(2)} />}
          {step === 2 && <ShippingStep shipping={shipping} setShipping={setShipping} onNext={() => setStep(3)} onBack={() => setStep(1)} />}
          {step === 3 && <PaymentStep card={card} setCard={setCard} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} onPay={handlePay} paying={paying} onBack={() => setStep(2)} />}
        </div>
        <OrderSummary cart={cart} cartTotal={cartTotal} />
      </div>
    </div>
  );
};

export default CheckoutFlow;
